import Branch16 from '@carbon/icons-react/es/branch/16';
import CaretDown16 from '@carbon/icons-react/es/caret--down/16';
import Draggable16 from '@carbon/icons-react/es/draggable/16';
import { DataNode } from 'antd/es/tree';
import { cloneDeep } from 'lodash';
import React, { FC, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FormQuestion, FormQuestionType } from 'api/form/types';
import { IconShell } from 'components/atoms/icon-shell';
import { toastService } from 'components/molecules/toast';
import { BodyShort01, HelperText01 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import {
  useGetFlattenedFormQuestions,
  useUpdateFieldQuestionPositionMutation,
} from 'queries/form-template';
import { MaxBranchingDepth } from '../constants';
import { FormBuilderRearrangeTree } from './tree-component';
import {
  computeMaxDepthAfterDrop,
  constructTreeData,
  DropNodeType,
  FormTreeDataNode,
  updateTreeData,
} from './utils';

interface Props {
  templateId: number;
}

const BranchOption = styled.span`
  ${BodyShort01};
  color: var(--gray-50);
  display: flex;
  align-items: center;
`;
const BranchOptionLabel = styled.span`
  padding-left: var(--spacing-04);
`;

const EmptyWrapper = styled.div`
  display: flex;
  align-items: center;
  vertical-align: middle;
  height: 100%;
  border: 1px solid var(--border-color);
  ${HelperText01};
  min-height: 400px;
`;

const EmptyWrapperContent = styled.div`
  margin: 0 auto;
`;

export const RearrangeTree: FC<Props> = ({ templateId }) => {
  const windowHeight = window.innerHeight;
  const modalFooterHeight = 104;
  const modalHeaderHeight = 58;
  const treeHeight =
    windowHeight * 0.75 - modalFooterHeight - modalHeaderHeight;

  const branchLimitReachedTitle = useTranslation(
    'kanban.error.drop_not_allowed.title',
  );

  const branchLimitReachedMessage = useTranslation(
    'form_templates.max_depth_limit_reached',
    { count: MaxBranchingDepth },
  );

  const flattenedQuestions = useGetFlattenedFormQuestions(templateId).filter(
    (question) => question.formQuestionType !== FormQuestionType.MatrixRow,
  );

  const ref = useRef<any>(null);

  // Add -1 for root level questions when setting the data for first time.
  const [flattenedTreeData, setFlattenedTreeData] = useState(
    cloneDeep(flattenedQuestions).map((question) => {
      if (!question.branchingFrom) {
        return {
          ...question,
          branchingFrom: -1,
        };
      }

      return question;
    }),
  );

  const { mutate: updateFieldQuestionMutation } =
    useUpdateFieldQuestionPositionMutation(templateId);

  const treeData = useMemo(() => {
    return constructTreeData(flattenedTreeData);
  }, [flattenedTreeData]);

  const onDrop = ({ node: dropNode, dragNode }) => {
    const maxDepth = computeMaxDepthAfterDrop(dragNode, dropNode);

    if (maxDepth > MaxBranchingDepth) {
      toastService({
        title: branchLimitReachedTitle,
        message: branchLimitReachedMessage,
      }).error();

      return;
    }
    const { branchingFrom, position, updatedFlattenedTreeData } =
      updateTreeData({ flattenedTreeData, dragNode, dropNode });

    setFlattenedTreeData(updatedFlattenedTreeData);

    const payload = {
      position,
    } as FormQuestion;

    if (dragNode.branchingFrom !== branchingFrom) {
      payload.branchingFrom = branchingFrom;
    }
    updateFieldQuestionMutation({
      questionId: dragNode.questionId,
      payload,
    });
  };

  return flattenedTreeData.length ? (
    <FormBuilderRearrangeTree
      ref={ref}
      treeData={treeData as unknown as DataNode[]}
      height={treeHeight}
      showIcon
      titleRender={(nodeData) => {
        return (nodeData as unknown as FormTreeDataNode).type ===
          DropNodeType.Question ? (
          <span>{nodeData.title}</span>
        ) : (
          <BranchOption>
            <Branch16 />
            <BranchOptionLabel>{nodeData.title}</BranchOptionLabel>
          </BranchOption>
        );
      }}
      icon={(props) => {
        const nodeData = props as unknown as FormTreeDataNode;

        return nodeData.type === DropNodeType.Question ? <Draggable16 /> : null;
      }}
      switcherIcon={<IconShell icon={<CaretDown16 />} />}
      onDragStart={({ event, node }) => {
        const nodeData = node as unknown as FormTreeDataNode;

        if (nodeData.type === DropNodeType.Option) {
          event.preventDefault();
        }
      }}
      onDragOver={({ node: { key } }) => {
        ref.current?.scrollTo({ key, offset: 10 });
      }}
      allowDrop={({ dropNode, dropPosition }) => {
        const nodeData = dropNode as unknown as FormTreeDataNode;

        // Block creation of child for a question
        if (nodeData.type === DropNodeType.Question && dropPosition === 0) {
          return false;
        }

        // Dropposition except first place will always return the parent question
        if (nodeData.type === DropNodeType.Option && dropPosition > 0) {
          return false;
        }

        return true;
      }}
      onDrop={onDrop}
      defaultExpandAll
      blockNode
      draggable
    />
  ) : (
    <EmptyWrapper>
      <EmptyWrapperContent>
        <FormattedMessage id="form_template.rearrange.empty" />
      </EmptyWrapperContent>
    </EmptyWrapper>
  );
};
